'use client'

import { useEffect, useMemo, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Check, type LucideIcon } from 'lucide-react'

interface QuizOption {
  id: string
  label: string
  description?: string
  icon?: string | LucideIcon
}

interface QuizOptionsProps {
  options: QuizOption[]
  selected: string | string[] | null
  onSelect: (id: string) => void
  multiSelect?: boolean
  columns?: 1 | 2
}

export function QuizOptions({
  options,
  selected,
  onSelect,
  multiSelect = false,
  columns = 1
}: QuizOptionsProps) {
  // Derivar selectedIds do prop selected
  const selectedIds = useMemo(() => {
    if (selected === null) return []
    if (Array.isArray(selected)) return selected
    return [selected]
  }, [selected])

  const isSelected = useCallback((id: string) => selectedIds.includes(id), [selectedIds])

  const handleSelect = useCallback((id: string) => {
    onSelect(id)
  }, [onSelect])

  // Keyboard shortcuts (A, B, C, D...)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toUpperCase()
      const index = key.charCodeAt(0) - 65 // A=0, B=1, C=2, D=3
      if (index >= 0 && index < options.length) {
        handleSelect(options[index].id)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [options, handleSelect])

  const getShortcutKey = (index: number) => String.fromCharCode(65 + index)

  return (
    <div className="w-full max-w-lg mx-auto">
      <div
        role="listbox"
        aria-label="Selecione uma opção"
        aria-multiselectable={multiSelect}
        className={`grid gap-3 sm:gap-4 ${columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}
      >
        {options.map((option, index) => (
          <motion.button
            key={option.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => handleSelect(option.id)}
            role="option"
            aria-selected={isSelected(option.id)}
            aria-label={`${option.label}${option.description ? ` - ${option.description}` : ''} - Tecla ${getShortcutKey(index)}`}
            className={`
              group relative flex items-center gap-4 p-4 md:p-4 min-h-[56px] rounded-xl
              text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500/50
              ${isSelected(option.id)
                ? 'bg-purple-500/20 border-2 border-purple-500 scale-[1.02] backdrop-blur-xl'
                : 'bg-white/85 dark:bg-white/5 backdrop-blur-xl border-2 border-purple-500/20 dark:border-white/10 hover:border-purple-500 hover:bg-purple-500/20 dark:hover:bg-purple-500/20'
              }
            `}
          >
            {/* Keyboard shortcut */}
            <div className={`
              shrink-0 w-8 h-8 rounded-lg flex items-center justify-center
              font-mono text-sm font-semibold transition-colors
              ${isSelected(option.id)
                ? 'bg-purple-500 text-white'
                : 'bg-indigo-950/10 dark:bg-white/10 text-indigo-950/50 dark:text-white/50 group-hover:bg-purple-500/20 group-hover:text-purple-600 dark:group-hover:text-purple-400'
              }
            `}>
              {getShortcutKey(index)}
            </div>

            {/* Icon */}
            {option.icon && (
              <span className="shrink-0">
                {typeof option.icon === 'string' ? (
                  <span className="text-2xl">{option.icon}</span>
                ) : (
                  <option.icon size={24} strokeWidth={2} className="text-purple-400" />
                )}
              </span>
            )}

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className={`font-medium transition-colors ${isSelected(option.id) ? 'text-purple-600 dark:text-white' : 'text-indigo-950/90 dark:text-white/90 group-hover:text-purple-600 dark:group-hover:text-purple-400'}`}>
                {option.label}
              </p>
              {option.description && (
                <p className="text-sm text-indigo-950/70 dark:text-white/50 mt-0.5">{option.description}</p>
              )}
            </div>

            {/* Check mark */}
            {isSelected(option.id) && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="shrink-0 w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center"
              >
                <Check size={14} className="text-white" />
              </motion.div>
            )}
          </motion.button>
        ))}
      </div>

      {/* Multi-select hint */}
      {multiSelect && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-indigo-950/30 dark:text-white/30 text-sm mt-4 text-center"
        >
          Selecione quantas opcoes quiser, depois pressione{' '}
          <kbd className="px-2 py-0.5 bg-indigo-950/10 dark:bg-white/10 rounded text-indigo-950/50 dark:text-white/50 text-xs font-mono">Enter</kbd>
        </motion.p>
      )}
    </div>
  )
}
